'use client';

import { useMemo } from 'react';
import { DEFAULT_CITY_SLUG, findCityById, CITIES } from '@/lib/config/cities';
import { useAiFoodOrderStore } from '@/store';

/**
 * App-flow navigation hook.
 *
 * Since the sitemap restructure (SITEMAP_RESTRUCTURE_PLAN.md) the app flow is
 * CITY-LESS: home is `/`, listings live under `/danh-sach-cua-hang`, and the
 * visitor's city is data (resolved from their saved address), not a URL
 * segment. The hook keeps its old name/shape so the ~48 existing call sites
 * work unchanged — only the returned URLs changed.
 *
 * `citySlug` is still exposed for components that need it as a data key
 * (analytics labels, per-city content). It comes from the store, never from
 * the URL.
 */
export function useCityNavigation() {
  const cityId = useAiFoodOrderStore.use.city_id();

  const citySlug = useMemo(
    () => findCityById(cityId)?.slug ?? DEFAULT_CITY_SLUG,
    [cityId]
  );

  const routes = useMemo(
    () => ({
      // Trang chủ — personalised by saved address, no city segment
      home: '/',
      solomeal: '/solomeal',

      // Danh sách cửa hàng (search)
      search: '/danh-sach-cua-hang',

      // Chi tiết cửa hàng
      storeDetail: (storeSlug: string) => `/cua-hang/${storeSlug}`,

      // Order routes cho luồng chính (desktop/mobile web)
      orderStore: (storeId: string) => `/order/${storeId}`,
      orderCart: (storeId: string) => `/order/${storeId}/cart`,

      // Order routes cho webview (luồng /m)
      orderStore_wv: (storeId: string) => `/m/order-wv/${storeId}`,
      orderCart_wv: (storeId: string) => `/m/order-wv/${storeId}/cart`,

      // User routes
      user: '/user',
      orderList: '/order-list',

      // Static routes
      sentryExamplePage: '/sentry-example-page',
      order: '/order',
      store: '/store',
    }),
    []
  );

  return {
    citySlug,
    routes,
    /**
     * Get city slug từ address text (legacy helper — data only, not routing)
     */
    getCitySlugFromAddress: (addressText: string) => {
      const matchedCity = CITIES.find((city) =>
        addressText.includes(city.label)
      );
      return matchedCity?.slug ?? DEFAULT_CITY_SLUG;
    },
  };
}

/**
 * Hook đơn giản chỉ để lấy routes của app flow.
 */
export function useCityRoutes() {
  const { routes } = useCityNavigation();
  return routes;
}
